import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Avatar,
  Box,
  Typography,
  CircularProgress,
  Paper,
  IconButton,
  Chip,
  Divider,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Alert,
  Button,
} from "@mui/material";
import ArrowBackIcon from "../assets/img/LeftIcon.svg";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { PanelGroup, Panel, PanelResizeHandle } from "react-resizable-panels";
import { useAppSelector, useAppDispatch } from "hooks";
import { Conversation, Note, TheoTicketCategory } from "../types/supervisorType";
import UserAvatar from "./UserAvatar";
import TicketWorklogSection, { TicketTimeEntry } from "./TicketWorklogSection";
import DOMPurify from "dompurify";
import TicketDetailSection from "./TicketDetailSection";
import { NEUTRAL, ORANGE, RADIUS, VIOLET } from "../styles/brandTokens";
import AssistantIcon from "../assets/img/assist_avatar.svg";
import {
  formatPropertyLabel,
  priorityDotColor,
  statusDotColor,
} from "./ticketProperties/propertyDots";
import { TypingIndicator } from "../shared/components";
import SupervisorAction from "../actions/supervisorAction";
import { ActionTypes } from "../store/actionTypes";
import { useUserMsp } from "../hooks/useUserMsp";
import { getPsaDisplayName, getTicketScopeLabel } from "../utils/mspUtils";
import TicketQualityReviewPanel from "./TicketQualityReviewAction";
import TicketAttachmentView from "./TicketAttachmentView";
import TicketDeviceProperties from "./TicketDeviceProperties";
const URL_RE = /https?:\/\/[^\s<>"']+/gi;

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const trimTrailingUrlPunctuation = (url: string) => url.replace(/[.,;!?)\]}'"]+$/g, "");

const plainTextToLinkedHtml = (text: string) =>
  escapeHtml(text).replace(URL_RE, (match) => {
    const trimmed = trimTrailingUrlPunctuation(match);
    const trailing = match.slice(trimmed.length);
    return `<a href="${trimmed}" target="_blank" rel="noopener noreferrer">${trimmed}</a>${trailing}`;
  });

// Helper to render HTML content safely, preserving newlines and bare URLs.
const renderHtmlContent = (htmlString: string) => {
  if (!htmlString) return null;
  
  // Check if content contains HTML tags
  const hasHtmlTags = /<[^>]+>/g.test(htmlString);
  
  // If it's plain text, convert newlines to <br> tags
  if (!hasHtmlTags) {
    const formattedContent = htmlString
      .split('\n')
      .map((line, index, array) => {
        // Don't add <br> after the last line
        const linkedLine = plainTextToLinkedHtml(line);
        return index < array.length - 1 ? `${linkedLine}<br />` : linkedLine;
      })
      .join('');
    return <div dangerouslySetInnerHTML={{ __html: formattedContent }} />;
  }
  
  // Sanitize rich notes while preserving line breaks and inline attachments.
  const formattedHtml = DOMPurify.sanitize(htmlString.replace(/\n/g, '<br />'));
  return <div dangerouslySetInnerHTML={{ __html: formattedHtml }} />;
};

const wrappedHtmlContentSx = {
  minWidth: 0,
  maxWidth: "100%",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
  whiteSpace: "normal",
  "& *": {
    minWidth: 0,
    maxWidth: "100%",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  "& a": {
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  "& img, & video, & iframe": {
    maxWidth: "100%",
    height: "auto",
  },
  "& pre, & code": {
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  "& table": {
    width: "100%",
    maxWidth: "100%",
    tableLayout: "fixed",
  },
  "& th, & td": {
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
} as const;

// Note visibility as a muted eyebrow label (normalized across all PSAs). Only "external"
// is orange: a customer can see it, which is the one case worth a second look.
const getNoteVisibilityBadge = (type?: string | null) => {
  // Normalized type values: "INTERNAL", "RESOLUTION", "PUBLIC"
  if (type === "SYSTEM") {
    return { label: "system", color: NEUTRAL.textMuted, isPrivate: true };
  }
  if (type === "INTERNAL" || type === "RESOLUTION") {
    return { label: "internal", color: NEUTRAL.textMuted, isPrivate: true };
  } else if (type === "PUBLIC") {
    return { label: "external", color: ORANGE.text, isPrivate: false };
  }

  // Default fallback
  return { label: "other", color: NEUTRAL.textMuted, isPrivate: false };
};

/** Matches the chat's Theo-authored check so a Theo note wears Theo's mark, not a person's. */
const isTheoNoteAuthor = (name?: string | null) => /^theo\b/i.test(String(name || "").trim());

const PROPERTY_DOT_SIZE = 7;

/** Status / priority value: a dot and a word, the same language as the chat header state. */
function PropertyDotValue({ label, color }: { label: string; color: string }) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, ml: 2, minWidth: 0 }}>
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          width: PROPERTY_DOT_SIZE,
          height: PROPERTY_DOT_SIZE,
          borderRadius: "50%",
          flexShrink: 0,
          backgroundColor: color,
        }}
      />
      <Typography
        component="span"
        sx={{
          fontFamily: "var(--theo-font-sans)",
          fontSize: 13,
          fontWeight: 500,
          color: NEUTRAL.text,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

const toTimestamp = (value?: string | null) => {
  const timestamp = value ? new Date(value).getTime() : Number.NaN;
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getTheoTicketCategories = (value: unknown): TheoTicketCategory[] =>
  (Array.isArray(value) ? value : []).filter(
    (category): category is TheoTicketCategory =>
      Boolean(
        category &&
          typeof category === "object" &&
          typeof (category as TheoTicketCategory).l1 === "string" &&
          typeof (category as TheoTicketCategory).l2 === "string"
      )
  );

interface TicketDetailProps {
  ticketId: string;
  onBack: () => void;
  /** Supervisor ticket + chat split: collapse entire ticket pane so chat uses full space. */
  onCollapseForChat?: () => void;
  /** Direction in which this ticket context pane collapses. */
  chatPlacement?: "right" | "below";
  /** Narrow right-side layout: prioritize conversation width over the properties column. */
  compactSidePane?: boolean;
}

const TicketDetail: React.FC<TicketDetailProps> = ({
  ticketId,
  onBack,
  onCollapseForChat,
  chatPlacement = "right",
  compactSidePane = false,
}) => {
  const { selectedTicket: storedTicket, isLoading, error, isRefreshing } = useAppSelector(
    (state) => state.supervisor
  );
  const selectedTicket = storedTicket && String(storedTicket.ticket?.id ?? storedTicket.id) === String(ticketId) ? storedTicket : null;
  const { userMspInfo } = useUserMsp();
  const currentUser = useAppSelector((state: any) => state.auth.user);
  const dispatch = useAppDispatch();
  const [propertiesExpanded, setPropertiesExpanded] = useState(!compactSidePane);
  const [localRefreshing, setLocalRefreshing] = useState(false);
  const [showSystemNotes, setShowSystemNotes] = useState(false);
  const [isSystemNotesLoading, setIsSystemNotesLoading] = useState(false);
  const didInitSystemNotesToggleRef = useRef(false);

  useEffect(() => {
    setPropertiesExpanded(!compactSidePane);
  }, [compactSidePane]);

  const scopeLabel = useMemo(
    () => getTicketScopeLabel(userMspInfo?.msp_type),
    [userMspInfo?.msp_type]
  );
  const isHaloTicket = useMemo(() => {
    const ticketSource = String(selectedTicket?.ticket?.source || "").trim().toLowerCase();
    const mspType = String(userMspInfo?.msp_type || "").trim().toLowerCase();
    return ticketSource === "halo" || mspType === "halo";
  }, [selectedTicket?.ticket?.source, userMspInfo?.msp_type]);
  const isConnectWiseTicket = useMemo(() => {
    const ticketSource = String(selectedTicket?.ticket?.source || "").trim().toLowerCase();
    const mspType = String(userMspInfo?.msp_type || "").trim().toLowerCase();
    return ticketSource === "connectwise" || mspType === "connectwise";
  }, [selectedTicket?.ticket?.source, userMspInfo?.msp_type]);
  const isAutotaskTicket = useMemo(() => {
    const ticketSource = String(selectedTicket?.ticket?.source || "").trim().toLowerCase();
    const mspType = String(userMspInfo?.msp_type || "").trim().toLowerCase();
    return ticketSource === "autotask" || mspType === "autotask";
  }, [selectedTicket?.ticket?.source, userMspInfo?.msp_type]);

  const worklogIdentity = `${currentUser?.email || ""}:${userMspInfo?.msp_id || ""}:${ticketId}`;
  const [worklogNotes, setWorklogNotes] = useState<{ key: string; ids: Set<string> }>();
  const onHaloEntriesChange = useCallback((entries: TicketTimeEntry[]) => {
    setWorklogNotes({ key: worklogIdentity, ids: new Set(entries.map(entry => String(entry.id))) });
  }, [worklogIdentity]);

  const renderNoteEntries = (notes: Note[] | undefined, emptyText: string) => {
    const noteList = Array.isArray(notes) ? notes : [];

    if (noteList.length === 0) {
      return (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center", mt: 4 }}
        >
          {emptyText}
        </Typography>
      );
    }

    return noteList.map((note: Note, index: number) => (
      <Box
        key={note.noteId}
        sx={{
          mb: 2,
          pb: 2,
          borderBottom:
            index < noteList.length - 1
              ? "1px dotted #eee"
              : "none",
          display: "flex",
          alignItems: "flex-start",
          backgroundColor: NEUTRAL.subtle,
          border: `1px solid ${NEUTRAL.border}`,
          borderRadius: `${RADIUS.card}px`,
          p: 2,
          minWidth: 0,
          maxWidth: "100%",
        }}
      >
        {isTheoNoteAuthor(note.user?.name) ? (
          <Avatar
            alt="Theo"
            sx={{ width: 24, height: 24, bgcolor: VIOLET.tint, mr: 1.5, flexShrink: 0 }}
          >
            <img src={AssistantIcon} alt="" width={16} height={16} />
          </Avatar>
        ) : (
          <UserAvatar
            name={note.user?.name || "User"}
            size={24}
            initials={note.user?.name?.[0] || "U"}
            sx={{ mr: 1.5 }}
          />
        )}
        <Box sx={{ flexGrow: 1, mt: 0.2, minWidth: 0 }}>
          <Box
            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  fontWeight: "600",
                  fontSize: "14px",
                  mr: 1,
                  color: NEUTRAL.text,
                }}
              >
                {note.user?.name || "Unknown User"}
              </Typography>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ height: 16, ml: 1, mr: 1, bgcolor: NEUTRAL.border }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  fontWeight: "500",
                  fontSize: "12px",
                  color: NEUTRAL.textMuted,
                }}
              >
                {new Date(note.time).toLocaleString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </Typography>
            </Box>
            {(() => {
              const badge = getNoteVisibilityBadge(note.type);
              return (
                <Typography
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.4,
                    flexShrink: 0,
                    fontFamily: "var(--theo-font-sans)",
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: badge.color,
                  }}
                >
                  {badge.isPrivate ? <LockOutlinedIcon sx={{ fontSize: 12 }} /> : null}
                  {badge.label}
                </Typography>
              );
            })()}
          </Box>
          <Box
            sx={{
              fontWeight: "400",
              fontSize: "16px",
              fontFamily: "var(--theo-font-sans)",
              color: "#4F5D70",
              ...wrappedHtmlContentSx,
            }}
          >
            {isAutotaskTicket && note.attachments?.length ? (
              <>
                {!String(note.noteId).startsWith("attachment-") && renderHtmlContent(note.content)}
                {note.attachments.map(attachment => <TicketAttachmentView
                  key={`${ticketId}:${attachment.attachmentId}`}
                  ticketId={ticketId}
                  attachment={attachment}
                />)}
              </>
            ) : renderHtmlContent(note.content)}
          </Box>
        </Box>
      </Box>
    ));
  };

  // Watch for refresh trigger and re-fetch ticket data silently (without global loading)
  useEffect(() => {
    if (isRefreshing && ticketId && currentUser?.email && !localRefreshing) {
      setLocalRefreshing(true);
      dispatch(
        SupervisorAction.refreshTicketById(ticketId, currentUser.email, {
          includeSystemNotes: showSystemNotes,
        }) as any
      )
        .then(() => {
          dispatch({ type: ActionTypes.TICKET_REFRESHING, payload: false });
          setLocalRefreshing(false);
        })
        .catch(() => {
          dispatch({ type: ActionTypes.TICKET_REFRESHING, payload: false });
          setLocalRefreshing(false);
        });
    }
  }, [isRefreshing, ticketId, currentUser?.email, dispatch, localRefreshing, showSystemNotes]);

  useEffect(() => {
    if (!ticketId || !currentUser?.email || isRefreshing || localRefreshing) {
      return;
    }
    // Skip first render: only trigger this loading behavior on actual checkbox toggles.
    if (!didInitSystemNotesToggleRef.current) {
      didInitSystemNotesToggleRef.current = true;
      return;
    }
    setIsSystemNotesLoading(true);
    setLocalRefreshing(true);
    dispatch(
      SupervisorAction.refreshTicketById(ticketId, currentUser.email, {
        includeSystemNotes: showSystemNotes,
      }) as any
    )
      .then(() => {
        setIsSystemNotesLoading(false);
        setLocalRefreshing(false);
      })
      .catch(() => {
        setIsSystemNotesLoading(false);
        setLocalRefreshing(false);
      });
  }, [showSystemNotes, ticketId, currentUser?.email, dispatch, isRefreshing]);

  if (error && !selectedTicket) {
    return <Box sx={{ p: 3 }}>
      <Alert severity="error">{error}</Alert>
      <Button onClick={() => dispatch(SupervisorAction.getTicketById(ticketId, currentUser?.email, { includeSystemNotes: showSystemNotes }) as any)}>
        Retry
      </Button>
    </Box>;
  }

  if (isLoading || (ticketId && !selectedTicket)) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedTicket) {
    return <Box sx={{ p: 3 }}>Ticket not found.</Box>;
  }

  const ticketRecord = selectedTicket.ticket;
  const descriptionConversation = selectedTicket.conversations?.find(
    (conversation: Conversation) => String(conversation.type || "").toLowerCase() === "description"
  );
  const connectWiseDescription = isConnectWiseTicket
    ? ticketRecord?.description || descriptionConversation?.content || ""
    : "";
  const headerTitle = `Ticket ${ticketRecord?.displayId || ticketRecord?.id || ticketId || ""}`;
  const psaName = getPsaDisplayName(ticketRecord?.source || userMspInfo?.msp_type);
  const psaUrl = selectedTicket.superopsUrl || selectedTicket.autotaskUrl;
  const headerSubtitle = isConnectWiseTicket
    ? ticketRecord?.title || ticketRecord?.summary || "No title"
    : ticketRecord?.title || "No title";
  const headerSubtitleStyles = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  };
  const connectWiseNotes = isConnectWiseTicket
    ? [
        ...(selectedTicket.notes || []),
        ...((selectedTicket.conversations || [])
          .filter((conversation: Conversation) => {
            const type = String(conversation.type || "").toLowerCase();
            return type !== "description";
          })
          .map((conversation: Conversation) => ({
            noteId: conversation.conversationId,
            content: conversation.content,
            time: conversation.time,
            user: conversation.user,
            type: "PUBLIC",
          } as Note))),
      ].sort((a, b) => toTimestamp(a.time) - toTimestamp(b.time))
    : [];

  const renderSystemNotesToggle = () => (
    <FormControlLabel
      control={<Checkbox size="small" checked={showSystemNotes} onChange={event => setShowSystemNotes(event.target.checked)} />}
      label="Show System Notes"
      sx={{ mr: 0.5, "& .MuiFormControlLabel-label": { fontFamily: "var(--theo-font-sans)", fontSize: "13px", color: "#666F8D" } }}
    />
  );

  const renderConversationNotesContent = () => (
    <Box sx={{ height: "100%", minWidth: 0, minHeight: 0, overflowX: "hidden", overflowY: "auto" }}>
      {/* Keep each scroll area usable when the surrounding ticket pane is short. */}
      <PanelGroup key={ticketId} direction="vertical" style={{ minHeight: isConnectWiseTicket ? 180 : 480 }}>
        <TicketDetailSection
          panelId="conversation"
          order={1}
          title={isHaloTicket ? "Feed" : isConnectWiseTicket ? "Notes" : "Conversation"}
          actions={isHaloTicket ? renderSystemNotesToggle() : undefined}
        >
          {isConnectWiseTicket && String(connectWiseDescription || "").trim() ? (
            <>
              <Box
                sx={{
                  backgroundColor: "transparent",
                  borderRadius: 0,
                  width: "auto",
                  height: "auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mt: 1.5,
                  mb: 2,
                  boxShadow: "none",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "var(--theo-font-sans)",
                    fontWeight: 700,
                    fontSize: "14px",
                    color: "#172033",
                    m: 0,
                  }}
                >
                  Description
                </Typography>
              </Box>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 1,
                  overflowX: "hidden",
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    fontWeight: "400",
                    fontSize: "16px",
                    fontFamily: "var(--theo-font-sans)",
                    color: "#666F8D",
                    lineHeight: 1.6,
                    ...wrappedHtmlContentSx,
                  }}
                >
                  {renderHtmlContent(connectWiseDescription)}
                </Box>
              </Paper>
            </>
          ) : null}
          {isHaloTicket && isSystemNotesLoading ? (
            <Box
              sx={{
                minHeight: 120,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={26} />
            </Box>
          ) : isHaloTicket ? (
            renderNoteEntries(selectedTicket.notes?.filter((note: Note) => worklogNotes?.key !== worklogIdentity || !worklogNotes.ids.has(String(note.noteId))), "No feed available.")
          ) : isConnectWiseTicket ? (
            renderNoteEntries(connectWiseNotes, "No notes available.")
          ) : selectedTicket.conversations?.length > 0 ? (
            selectedTicket.conversations.map(
              (msg: Conversation, index: number) => (
                <Box
                  key={msg.conversationId}
                  sx={{
                    mb: 2,
                    pb: 2,
                    borderBottom:
                      index < selectedTicket.conversations.length - 1
                      ? "1px solid #EEF2F6"
                        : "none",
                    display: "flex",
                    alignItems: "flex-start",
                    backgroundColor:
                      msg.type === "ASSISTANT" ? "#F5F3FF" : "inherit",
                    borderRadius: msg.type === "ASSISTANT" ? "10px" : 0,
                    p: msg.type === "ASSISTANT" ? 2 : 0,
                    minWidth: 0,
                    maxWidth: "100%",
                  }}
                >
                  <UserAvatar
                    name={
                      msg.type === "ASSISTANT"
                        ? "Assistant"
                        : msg.user?.name || "User"
                    }
                    size={24}
                    initials={msg.type === "ASSISTANT" ? "AI" : undefined}
                    bgColor={msg.type === "ASSISTANT" ? VIOLET.main : undefined}
                    sx={{ mr: 1.5 }}
                  />
                  <Box sx={{ flexGrow: 1, mt: 0.2, minWidth: 0 }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "var(--theo-font-sans)",
                          fontWeight: "600",
                          fontSize: "14px",
                          mr: 1,
                          color: "#172033",
                        }}
                      >
                        {msg.user?.name ||
                          (msg.type === "ASSISTANT"
                            ? "Theo Assistant"
                            : "Unknown User")}
                      </Typography>
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ height: 16, ml: 1, mr: 1, bgcolor: "#F0F2F5" }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "var(--theo-font-sans)",
                          fontWeight: "500",
                          fontSize: "12px",
                          color: "#666F8D",
                        }}
                      >
                        {new Date(msg.time).toLocaleString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        fontWeight: "400",
                        fontSize: "16px",
                        fontFamily: "var(--theo-font-sans)",
                        color: "#4F5D70",
                        ...wrappedHtmlContentSx,
                      }}
                    >
                      {renderHtmlContent(msg.content)}
                    </Box>
                  </Box>
                </Box>
              )
            )
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", mt: 4 }}
            >
              No conversation history available.
            </Typography>
          )}
        </TicketDetailSection>
        {!isHaloTicket && !isConnectWiseTicket && (
          <TicketDetailSection panelId="notes" order={2} title="Notes" divider actions={isAutotaskTicket ? renderSystemNotesToggle() : undefined}>
            {isAutotaskTicket && isSystemNotesLoading ? (
              <Box
                sx={{
                  minHeight: 120,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={26} />
              </Box>
            ) : (
              renderNoteEntries(selectedTicket.notes, "No notes available.")
            )}
          </TicketDetailSection>
        )}
        {(isAutotaskTicket || isHaloTicket) && (
          <TicketWorklogSection
            ticketId={ticketId}
            identity={`${currentUser?.email || ""}:${userMspInfo?.msp_id || ""}`}
            refreshKey={selectedTicket}
            onEntriesChange={isHaloTicket ? onHaloEntriesChange : undefined}
            renderContent={isHaloTicket ? entry => {
              const note = selectedTicket.notes?.find((note: Note) => String(note.noteId) === String(entry.id));
              return note ? <Box sx={{ ...wrappedHtmlContentSx, fontSize: 14, color: "#4F5D70" }}>{renderHtmlContent(note.content)}</Box> : null;
            } : undefined}
            resizable
          />
        )}
      </PanelGroup>
    </Box>
  );

  // Resizable horizontal layout:
  // left side = conversation/notes stack, right side = ticket properties.
  return (
    <Box
      sx={{
        flexGrow: 1,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
        overflowY: "auto",
        minHeight: 0,
        backgroundColor: "#FFFFFF",
        border: "1px solid #E2E8F0",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
        overflow: "hidden",
      }}
    >
      {/* Header — navbar: back • title • optional “focus chat” collapse (supervisor split). */}
      <Box
        sx={{
          px: 2.25,
          py: 1.75,
          background: "#FFFFFF",
          borderBottom: "1px solid #E8ECF2",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 0.5,
            width: "100%",
            gap: 0.5,
          }}
        >
          <IconButton
            onClick={onBack}
            sx={{
              width: 36,
              height: 36,
              minWidth: 36,
              minHeight: 36,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 0.5,
              p: 0,
              transition: "background 0.2s",
              "&:hover": {
                backgroundColor: "#F1F5F9",
              },
            }}
          >
            <img
              src={ArrowBackIcon}
              alt="Arrow Back"
              style={{ width: 20, height: 20 }}
            />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Source eyebrow: the pane mirrors the PSA record, so edits happen there, not here. */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25, minWidth: 0 }}>
              <Tooltip title={`View only. Edit this ticket in ${psaName}.`}>
                <Typography
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    fontFamily: "var(--theo-font-sans)",
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: NEUTRAL.textMuted,
                    whiteSpace: "nowrap",
                  }}
                >
                  <LockOutlinedIcon sx={{ fontSize: 12 }} />
                  {psaName} record
                </Typography>
              </Tooltip>
              {psaUrl ? (
                <Typography
                  component="a"
                  href={psaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    ml: "auto",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.4,
                    fontFamily: "var(--theo-font-sans)",
                    fontSize: 12,
                    fontWeight: 500,
                    color: VIOLET.main,
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                    borderRadius: `${RADIUS.control}px`,
                    "&:hover": { color: VIOLET.dark, textDecoration: "underline" },
                    "&:focus-visible": { outline: `2px solid ${VIOLET.main}`, outlineOffset: 2 },
                  }}
                >
                  Open in {psaName}
                  <OpenInNewIcon sx={{ fontSize: 13 }} />
                </Typography>
              ) : null}
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "var(--theo-font-sans)",
                fontWeight: "700",
                fontSize: 16,
                color: "text.primary",
                letterSpacing: "-0.01em",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {headerTitle}
            </Typography>
             {psaUrl ? (
               <Typography
                 component="a"
                 href={psaUrl}
                 target="_blank"
                 rel="noopener noreferrer"
                 variant="subtitle1"
                 sx={{
                   fontFamily: "var(--theo-font-sans)",
                 fontWeight: "500",
                 fontSize: "14px",
                 color: "primary.main",
                 textDecoration: "none",
                   cursor: "pointer",
                   ...headerSubtitleStyles,
                   "& a": {
                     color: "inherit",
                   },
                   "&:hover": {
                     textDecoration: "underline",
                   },
                 }}
               >
                {headerSubtitle}
              </Typography>
            ) : (
              <Typography
                variant="subtitle1"
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  fontWeight: "500",
                  fontSize: "14px",
                  color: "text.secondary",
                  ...headerSubtitleStyles,
                }}
              >
                {headerSubtitle}
              </Typography>
            )}
          </Box>
          <TicketQualityReviewPanel
            ticketId={ticketId}
            payload={selectedTicket}
            customerId={
              selectedTicket?.ticket && typeof selectedTicket.ticket === "object"
                ? ((selectedTicket.ticket as Record<string, unknown>).customer_id as number | string | null) ?? null
                : null
            }
          />
          {onCollapseForChat ? (
            <Tooltip title="Focus chat (hide conversation & properties)">
              <IconButton
                size="small"
                onClick={onCollapseForChat}
                aria-label="Focus chat, hide ticket details"
                sx={{
                  flexShrink: 0,
                  backgroundColor: "transparent",
                  color: NEUTRAL.textSecondary,
                  borderRadius: `${RADIUS.control}px`,
                  "&:hover": { backgroundColor: NEUTRAL.hover, color: NEUTRAL.text },
                }}
              >
                {chatPlacement === "below" ? (
                  <KeyboardArrowDownIcon sx={{ fontSize: 22 }} />
                ) : (
                  <ChevronRightIcon sx={{ fontSize: 22 }} />
                )}
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: "hidden" }}>
        {propertiesExpanded ? (
          <PanelGroup direction="horizontal">
            <Panel defaultSize={66.67} minSize={20}>
              {renderConversationNotesContent()}
            </Panel>
            <PanelResizeHandle
              style={{
                width: "4px",
                background: "#F0F2F5",
              }}
            />
            <Panel defaultSize={33.33} minSize={12}>
            <Box
              sx={{
                height: "100%",
                minWidth: 0,
                background: "#FFFFFF",
                p: 0,
                display: "flex",
                flexDirection: "column",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Toggle Button */}
              <IconButton
                onClick={() => setPropertiesExpanded(false)}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 44,
                  zIndex: 10,
                  backgroundColor: "transparent",
                  color: NEUTRAL.textSecondary,
                  borderRadius: `${RADIUS.control}px`,
                  "&:hover": {
                    backgroundColor: NEUTRAL.hover,
                  },
                  width: 32,
                  height: 32,
                }}
              >
                <ChevronRightIcon sx={{ fontSize: 20 }} />
              </IconButton>

            <PanelGroup direction="vertical">
              <TicketDetailSection title="Ticket Properties" panelId="ticket-properties" order={1}
                actions={(isRefreshing || localRefreshing) ? <TypingIndicator /> : undefined}>
            {/* Status */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography
            sx={{
              color: NEUTRAL.textMuted,
              fontFamily: "var(--theo-font-sans)",
              fontWeight: 500,
              fontSize: "12px",
              minWidth: 90,
            }}
          >
            Status
          </Typography>
            {selectedTicket?.ticket?.status && (
            <PropertyDotValue
              label={formatPropertyLabel(selectedTicket?.ticket?.status)}
              color={statusDotColor(selectedTicket?.ticket?.status)}
            />
          )}
        </Box>
        {/* Assigned to */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography
            sx={{
              color: NEUTRAL.textMuted,
              fontFamily: "var(--theo-font-sans)",
              fontWeight: 500,
              fontSize: "12px",
              minWidth: 90,
            }}
          >
            Assigned to
          </Typography>
          {selectedTicket?.ticket?.assigned_to ? (
            <UserAvatar
              name={selectedTicket.ticket.assigned_to.name}
              avatarUrl={selectedTicket.ticket.assigned_to.avatarUrl}
              showName={true}
            />
          ) : null}
        </Box>
        {/* Requested by */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography
            sx={{
              color: NEUTRAL.textMuted,
              fontFamily: "var(--theo-font-sans)",
              fontWeight: 500,
              fontSize: "12px",
              minWidth: 90,
            }}
          >
            Requested by
          </Typography>
          <Typography
            sx={{
              fontWeight: 500,
              color: "#0B0D1E",
              fontSize: 12,
              fontFamily: "inter",
              ml: 2,
            }}
          >
            {selectedTicket?.ticket?.requester?.name || "-"}
          </Typography>
        </Box>
        {/* Impact */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography
            sx={{
              color: NEUTRAL.textMuted,
              fontFamily: "var(--theo-font-sans)",
              fontWeight: 500,
              fontSize: "12px",
              minWidth: 90,
            }}
          >
            Impact
          </Typography>
          <Typography
            sx={{
              fontWeight: 500,
              color: "#0B0D1E",
              fontSize: 12,
              fontFamily: "inter",
              ml: 2,
            }}
          >
            {selectedTicket?.ticket?.impact || "-"}
          </Typography>
        </Box>
        {/* Client */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography
            sx={{
              color: NEUTRAL.textMuted,
              fontFamily: "var(--theo-font-sans)",
              fontWeight: 500,
              fontSize: "12px",
              minWidth: 90,
            }}
          >
            Client
          </Typography>
          <Typography
            sx={{
              fontWeight: 500,
              color: "#0B0D1E",
              fontSize: 12,
              fontFamily: "inter",
              ml: 2,
            }}
          >
            {selectedTicket?.ticket?.client?.name ||
              selectedTicket?.ticket?.company?.name ||
              selectedTicket?.ticket?.site?.name ||
              "-"}
          </Typography>
        </Box>
        {/* Start date */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography
            sx={{
              color: NEUTRAL.textMuted,
              fontFamily: "var(--theo-font-sans)",
              fontWeight: 500,
              fontSize: "12px",
              minWidth: 90,
            }}
          >
            Start date
          </Typography>
          <Typography
            sx={{
              fontWeight: 500,
              color: "#0B0D1E",
              fontSize: 12,
              fontFamily: "inter",
              ml: 2,
            }}
          >
            {selectedTicket?.ticket?.start_date
              ? new Date(selectedTicket.ticket.start_date).toLocaleDateString(
                  undefined,
                  { year: "numeric", month: "long", day: "numeric" }
                )
              : "-"}
          </Typography>
        </Box>
        {/* Due date */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography
            sx={{
              color: NEUTRAL.textMuted,
              fontFamily: "var(--theo-font-sans)",
              fontWeight: 500,
              fontSize: "12px",
              minWidth: 90,
            }}
          >
            Due date
          </Typography>
          <Typography
            sx={{
              fontWeight: 500,
              color: "#0B0D1E",
              fontSize: 12,
              fontFamily: "inter",
              ml: 2,
            }}
          >
            {selectedTicket?.ticket?.due_date
              ? new Date(selectedTicket.ticket.due_date).toLocaleDateString(
                  undefined,
                  { year: "numeric", month: "long", day: "numeric" }
                )
              : "-"}
          </Typography>
        </Box>
        {/* Priority */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography
            sx={{
              color: NEUTRAL.textMuted,
              fontFamily: "var(--theo-font-sans)",
              fontWeight: 500,
              fontSize: "12px",
              minWidth: 90,
            }}
          >
            Priority
          </Typography>
          {selectedTicket?.ticket?.priority && (
            <PropertyDotValue
              label={formatPropertyLabel(selectedTicket?.ticket?.priority)}
              color={priorityDotColor(selectedTicket?.ticket?.priority)}
            />
          )}
        </Box>
        {/* Queue/Board */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography
            sx={{
              color: NEUTRAL.textMuted,
              fontFamily: "var(--theo-font-sans)",
              fontWeight: 500,
              fontSize: "12px",
              minWidth: 90,
            }}
          >
            {scopeLabel}
          </Typography>
          {selectedTicket?.ticket?.queue && (
            <Typography
              sx={{
                ml: 2,
                fontFamily: "var(--theo-font-sans)",
                fontSize: 13,
                fontWeight: 500,
                color: NEUTRAL.text,
              }}
            >
              {selectedTicket?.ticket?.queue}
            </Typography>
          )}
        </Box>
        {getTheoTicketCategories(selectedTicket?.ticket?.theo_category).length > 0 ? (
          <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
            <Typography
              sx={{
                color: NEUTRAL.textMuted,
                fontFamily: "var(--theo-font-sans)",
                fontWeight: 500,
                fontSize: "12px",
                minWidth: 90,
                pt: 0.75,
              }}
            >
              Theo Categories
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, ml: 2, minWidth: 0, flex: 1 }}>
              {getTheoTicketCategories(selectedTicket?.ticket?.theo_category).map((category) => (
                <Chip
                  key={`${category.l1}-${category.l2}`}
                  label={`${category.l1} > ${category.l2}`}
                  size="small"
                  sx={{
                    background: VIOLET.wash,
                    border: `1px solid ${VIOLET.tint}`,
                    color: VIOLET.theoText,
                    borderRadius: `${RADIUS.control}px`,
                    fontWeight: 500,
                    fontSize: 11,
                    height: "auto",
                    minHeight: 24,
                    maxWidth: "100%",
                    "& .MuiChip-label": {
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      py: 0.5,
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        ) : null}
              </TicketDetailSection>
              {selectedTicket?.ticket?.device ? (
                <TicketDetailSection title="Device Properties" panelId="ticket-device-properties" order={2} divider>
                  <TicketDeviceProperties device={selectedTicket?.ticket?.device} />
                </TicketDetailSection>
              ) : null}
            </PanelGroup>
            </Box>
            </Panel>
          </PanelGroup>
        ) : (
          <Box sx={{ height: "100%", display: "flex", minWidth: 0 }}>
            <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
              {renderConversationNotesContent()}
            </Box>
            <Box
              sx={{
                width: 48,
                minWidth: 48,
                background: "#FFF",
                borderLeft: "1px solid #F0F2F5",
                position: "relative",
              }}
            >
              <IconButton
                onClick={() => setPropertiesExpanded(true)}
                sx={{
                  position: "absolute",
                  top: 8,
                  left: 8,
                  zIndex: 10,
                  backgroundColor: "transparent",
                  color: NEUTRAL.textSecondary,
                  borderRadius: `${RADIUS.control}px`,
                  "&:hover": {
                    backgroundColor: NEUTRAL.hover,
                  },
                  width: 32,
                  height: 32,
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TicketDetail;
